import React from 'react';
import UndoIcon from '@mui/icons-material/Undo';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ActionMode } from '../../hooks/useUPCActions';

const kbdStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 16, padding: '0 3px', height: 16,
  background: 'rgba(232,151,108,0.15)', border: '1px solid rgba(232,151,108,0.3)',
  borderRadius: 3, fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700,
  color: 'var(--blue)', lineHeight: 1, flexShrink: 0,
};

const actionBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px',
  background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
  color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'var(--font-body)',
};

export interface CropActionBarProps {
  mode: ActionMode;
  selectedCropIds: Set<string>;
  newDesignOptions?: { itemNumber: string; label: string }[];
  // Action callbacks
  onDeactivate: () => void;
  onSetRep?: { designId: string; cropId: string } | null;
  onSetRepClick?: (designId: string, cropId: string) => void;
  onNewDesignClick?: (itemNumber: string) => void;
  // Undo
  undoCount: number;
  onUndo: () => void;
  // Optional idle content (highlight nav for review, empty for master label)
  idleContent?: React.ReactNode;
  // Info button
  onShowInfo?: () => void;
}

export const CropActionBar: React.FC<CropActionBarProps> = ({
  mode,
  selectedCropIds,
  newDesignOptions = [],
  onDeactivate,
  onSetRep,
  onSetRepClick,
  onNewDesignClick,
  undoCount,
  onUndo,
  idleContent,
  onShowInfo,
}) => {
  const rightControls = (
    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      {undoCount > 0 && (
        <button onClick={onUndo} style={actionBtnStyle}>
          <UndoIcon style={{ fontSize: 12 }} />
          <span style={kbdStyle}>⌘Z</span>
        </button>
      )}
      {onShowInfo && (
        <button
          onClick={onShowInfo}
          title="Task guide (i)"
          style={{
            ...actionBtnStyle, padding: '3px 6px',
          }}
        >
          <InfoOutlinedIcon style={{ fontSize: 12 }} />
        </button>
      )}
    </div>
  );

  const barStyle: React.CSSProperties = {
    flexShrink: 0, borderTop: '1px solid var(--border)',
    padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8,
    minHeight: 36,
  };

  const shortcutHintStyle: React.CSSProperties = {
    ...actionBtnStyle,
    cursor: 'default',
  };

  // ── Crops selected mode ──
  if (mode === 'crops-selected') {
    return (
      <div style={{ ...barStyle, borderTopColor: 'rgba(232,151,108,0.3)', background: 'rgba(232,151,108,0.06)' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--blue)' }}>
          {selectedCropIds.size} crop{selectedCropIds.size > 1 ? 's' : ''} selected
        </span>
        <div style={{ width: 1, height: 18, background: 'rgba(232,151,108,0.2)', flexShrink: 0 }} />
        <span style={{ fontSize: 10, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>Click a section to move</span>
        <div style={{ width: 1, height: 18, background: 'rgba(232,151,108,0.2)', flexShrink: 0 }} />
        {newDesignOptions.length > 0 && onNewDesignClick && newDesignOptions.map((option, index) => (
          <button key={option.itemNumber} onClick={() => onNewDesignClick(option.itemNumber)} style={actionBtnStyle}>
            {index === 0 && <span style={kbdStyle}>D</span>}
            {option.label}
          </button>
        ))}
        <button onClick={() => {}} style={shortcutHintStyle} tabIndex={-1} aria-hidden="true">
          <span style={kbdStyle}>C</span>
          Cartons
        </button>
        <button onClick={() => {}} style={shortcutHintStyle} tabIndex={-1} aria-hidden="true">
          <span style={kbdStyle}>N</span>
          Non-front
        </button>
        <button onClick={() => {}} style={shortcutHintStyle} tabIndex={-1} aria-hidden="true">
          <span style={kbdStyle}>I</span>
          Inactive
        </button>
        {onSetRep && onSetRepClick && (
          <button onClick={() => onSetRepClick(onSetRep.designId, onSetRep.cropId)} style={actionBtnStyle}>
            <span style={kbdStyle}>R</span>
            Set rep
          </button>
        )}
        <button onClick={() => {}} style={shortcutHintStyle} tabIndex={-1} aria-hidden="true">
          <span style={kbdStyle}>Esc</span>
          Deselect
        </button>
        {rightControls}
      </div>
    );
  }

  // ── Idle mode ──
  return (
    <div style={barStyle}>
      {idleContent}
      {rightControls}
    </div>
  );
};
